import { useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';
import { useTourStore } from './useTourStore';
import { TOURS, localizeStep } from './tourContent';

/**
 * Imperative driver.js wrapper. `startTour(id)` builds a localized, branded
 * walkthrough for the given tour, skipping any step whose anchor isn't in the
 * DOM yet (graceful on async data / permission-gated UI). Marking the tour as
 * "seen" happens on destroy, so it won't auto-replay next visit.
 */
export function useTour() {
  const lang = usePrefsStore((s) => s.language);
  const t = useT();
  const markSeen = useTourStore((s) => s.markSeen);

  const startTour = useCallback(
    (tourId: string): boolean => {
      const def = TOURS[tourId];
      if (!def) return false;

      const steps = def.steps
        .filter((s) => !s.element || document.querySelector(s.element))
        .map((s) => localizeStep(s, lang));

      // Nothing anchorable yet (page still loading) — let the caller retry.
      if (steps.length === 0) return false;

      const d = driver({
        showProgress: true,
        progressText: t('tour.progress'),
        nextBtnText: t('tour.next'),
        prevBtnText: t('tour.prev'),
        doneBtnText: t('tour.done'),
        allowClose: true,
        overlayColor: 'rgba(8, 5, 16, 0.72)',
        stagePadding: 6,
        stageRadius: 12,
        popoverClass: 'xk-tour',
        steps,
        onDestroyed: () => {
          markSeen(tourId);
        },
      });
      d.drive();
      return true;
    },
    [lang, t, markSeen],
  );

  return { startTour };
}
